import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, FolderKanban, ListChecks, StickyNote, MapPin, Archive } from "lucide-react";
import { api } from "@/lib/api";
import { can } from "@/lib/colors";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Pill, Empty } from "@/components/common";
import { ProjectDialog, PROJECT_STATUS } from "@/components/projects/ProjectDialog";
import { Progress } from "@/components/projects/ProjectChecklist";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const ProjectCard = ({ p }) => {
  const loc = p.kind === "location";
  const Icon = p.archived ? Archive : loc ? MapPin : FolderKanban;
  const pill = p.archived ? { label: "Archived", cls: "bg-slate-100 text-slate-500 border-slate-200" } : loc ? { label: "Location", cls: "bg-[#EAF2F2] text-[#24474A] border-[#24474A]/20" } : PROJECT_STATUS[p.status];
  return (
  <Link to={`/projects/${p.id}`} className={`block rounded-2xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md fade-up ${p.archived ? "opacity-75" : ""}`} data-testid={`project-card-${p.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${loc ? "bg-[#EAF2F2] text-[#24474A]" : "bg-violet-50 text-violet-700"}`}><Icon className="w-5 h-5" /></span>
        <h3 className="font-bold text-lg text-slate-800 truncate">{p.name}</h3>
      </div>
      <Pill cls={pill.cls}>{pill.label}</Pill>
    </div>
    {p.description && <p className="text-sm text-slate-600 mt-3 line-clamp-2">{p.description}</p>}
    {p.checklist && <div className="mt-4"><div className="text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-1">Checklist</div><Progress done={p.checklist.done} total={p.checklist.total} testid={`project-card-progress-${p.id}`} /></div>}
    <div className="flex gap-4 mt-4 text-xs text-slate-500">
      <span className="flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> {p.open_tasks} open / {p.task_count} tasks</span>
      <span className="flex items-center gap-1"><StickyNote className="w-3.5 h-3.5" /> {p.note_count} notes</span>
    </div>
  </Link>
  );
};

const FILTERS = {
  active: ["Active projects", (p) => p.kind !== "location" && !p.archived && p.status !== "on_hold"],
  on_hold: ["On hold", (p) => p.kind !== "location" && !p.archived && p.status === "on_hold"],
  locations: ["Locations", (p) => p.kind === "location"],
  archived: ["Archived", (p) => p.archived],
};

export default function Projects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("active");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/projects");
    setProjects(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const list = projects.filter(FILTERS[filter][1]);
  return (
    <div>
      <PageHeader title="Projects/Locations" subtitle="Group tasks by project or by location, like Head office, and keep notes in one place.">
        {can(user, "manage_projects") && <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="new-project-btn"><Plus className="w-4 h-4 mr-1.5" /> New project/location</Button>}
      </PageHeader>
      <Tabs value={filter} onValueChange={setFilter} className="mb-5">
        <TabsList className="rounded-full flex-wrap h-auto">
          {Object.entries(FILTERS).map(([k, [l]]) => (
            <TabsTrigger key={k} value={k} className="rounded-full" data-testid={`projects-filter-${k}`}>{l} <span className="ml-1.5 text-slate-400">{projects.filter(FILTERS[k][1]).length}</span></TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="projects-list">
        {list.map((p) => <ProjectCard key={p.id} p={p} />)}
      </div>
      {list.length === 0 && <Empty testid="projects-empty">Nothing here yet.</Empty>}
      <ProjectDialog open={open} onClose={() => setOpen(false)} onSaved={load} />
    </div>
  );
}
